import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Checkbox,
    Image, ImageFit, Link,
    MessageBar, MessageBarType,
    Panel,
    PanelType, Pivot, PivotItem, SpinButton, Stack, Text, Toggle,
} from "@fluentui/react";
import api, {
    useGetExtensionFolioQuery,
    useLazyGetExportExtensionOptionsQuery,
    usePutExtensionFolioMutation,
    useRunImportExtensionsForFolioMutation,
    useValidateExtensionFolioMutation
} from "../../store/Api";
import {useDispatch} from "react-redux";
import {IsMutationSuccess} from "../../logic/MutationTypeChecker";
import {IAdditionalData, IInstalledExtension, IInstalledExtensionFolio, ISubExtension, IValidateExtensionResponse} from "../../models/Extension";
import ExtensionActivities, {IExtensionRunTarget} from "./ExtensionActivities";
import {
    CredentialsFailedBadge,
    FolioScheduleSetting,
    CredentialsVerifiedBadge,
    ExtensionFolioInstallButtons,
    ExtensionFolioInstallForm,
    MissingRequiredSettingsBadge,
    SubExtensionInstallForm,
    isRenderableStaticField
} from "../ExtensionInstallation/ExtensionDataForm";
import {LabeledCheckboxGroup} from "../CheckboxGroup";
import {DynamicSetting} from "../Common/Setting";
import ConditionalSetting from "../Common/ConditionalSetting";
import {DefaultButton} from "@fluentui/react";
import {toast} from "react-toastify";
import {useRunCrmFolioSyncMutation} from "../../store/endpoints/RecordLinksEndpoints";
import {cloneDeep, debounce, forEach} from "lodash";
import {PLACEHOLDER_IMAGE} from "../../Constants";
import ExtensionValidationErrorModal from "../ExtensionInstallation/ExtensionValidationErrorModal";
import ExtensionCategoryMappingModal from "../ExtensionInstallation/ExtensionCategoryMappingModal";
import ExtensionSegmentMappingTable from "../ExtensionInstallation/ExtensionSegmentMappingTable";
import {useUrlPanelState} from "../../hooks/useUrlState";
import AccordionBox from "../Common/AccordionBox";
import PsaQuoteLinkDialog from "../ExtensionModal/PsaQuoteLinkDialog";

/** Matches ScheduledExtensionRunner.DEFAULT_PURCHASE_HISTORY_MONTHS_BACK on the API side. */
const DEFAULT_PURCHASE_HISTORY_MONTHS = 12;

interface IExtensionEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    installedExtension?: IInstalledExtension;
    extensionId: string;
    isNew: boolean;
}


const ExtensionFolioDetailsPanel: React.FC<IExtensionEditModalProps> = (props) => {
    const extensionFolio = useGetExtensionFolioQuery(props.installedExtension?.source ?? props.extensionId, {skip: !(props.installedExtension?.source ?? props.extensionId)});
    /**
     * currentData, NOT data. They differ in exactly the case this panel lives in: `data` is the last
     * result the hook loaded for ANY argument, so while a second folio is being fetched it still holds the
     * first one's. Opening one folio after another therefore rendered the previous folio's capabilities,
     * settings and validation state for the length of the request before they were replaced.
     *
     * `currentData` is scoped to the argument in hand — undefined until this folio arrives, which is what
     * the loading branch below is for. It stays populated through a refetch of the same folio, so saving
     * and validating do not blank the panel.
     */
    const folio = extensionFolio.currentData;
    // Driven by ?panelTab= when the panel was opened through the URL (matching ExtensionDetailsPanel),
    // so a link — e.g. the home page's "Fix credentials" — can land on a particular tab. Falls back to
    // local state when the panel is mounted without the URL param (e.g. from the CRM setup dialog).
    const {isOpen: urlDriven, panelTab, setPanelTab} = useUrlPanelState('extensionFolio');
    const [localPivot, setLocalPivot] = useState<string>('edit');
    const selectedPivot = urlDriven ? (panelTab ?? 'edit') : localPivot;
    const setSelectedPivot = urlDriven ? setPanelTab : setLocalPivot;
    const [validationResponse, setValidationResponse] = useState<IValidateExtensionResponse | undefined>(undefined);
    const [enabledSubExtensions, setEnabledSubExtensions] = useState<string[] | undefined>(undefined);
    const [syncAllCompanies, setSyncAllCompanies] = useState<boolean>(false);
    const [syncAllContacts, setSyncAllContacts] = useState<boolean>(false);
    const [syncAllLocations, setSyncAllLocations] = useState<boolean>(false);
    const [purchaseHistoryMonthsBack, setPurchaseHistoryMonthsBack] = useState<number>(DEFAULT_PURCHASE_HISTORY_MONTHS);
    const [globalStorefrontMinimumPrice, setGlobalStorefrontMinimumPrice] = useState<boolean>(false);
    const [subExtensionData, setSubExtensionData] = useState<{ [key: string]: ISubExtension }>();
    const [subExtensionLabelMap, setSubExtensionLabelMap] = useState<{[key: string]: string}>();
    const [subExtensionDescriptions, setSubExtensionDescriptions] = useState<{[key: string]: string}>();
    const [showMoreErrorInfo, setShowMoreErrorInfo] = useState<boolean>(false);
    const [showCategoryMappings, setShowCategoryMappings] = useState<boolean>(false);
    const [mappingSubExtension, setMappingSubExtension] = useState<ISubExtension | undefined>(undefined);
    const [externalProductSegments, setExternalProductSegments] = useState<{ segmentName: string, id: string }[]>([]);
    const [externalProductTypes, setExternalProductTypes] = useState<{ typeName: string, id: string }[]>([]);
    const [mappingSegmentFilter, setMappingSegmentFilter] = useState<string | undefined>(undefined);
    const [showCategoryMappingsModal, setShowCategoryMappingsModal] = useState<boolean>(false);
    const [quoteLinkOpen, setQuoteLinkOpen] = useState<boolean>(false);

    // The quote-link quick flow only offers itself when the folio declares it (quoteLink names the
    // target sub-extension and carries the provider's install wording), so nothing here is
    // provider-specific.
    const folioQuoteLink = extensionFolio.data?.sourceExtensionFolio?.quoteLink;
    const opportunityImportExtensionId = folioQuoteLink?.subExtension
        ? `folio_${props.installedExtension?.source ?? props.extensionId}_${folioQuoteLink.subExtension}`
        : undefined;
    
    const [mappingFetcher] = useLazyGetExportExtensionOptionsQuery();
    const dispatch = useDispatch();

    // Validate-first mode (AC-1067): the last credential check failed, so nothing on this folio saves and
    // no option lookups run until a validation passes. Credential edits accumulate here, unsaved, and are
    // what the Validate button checks; the pass is what persists them.
    // The folio-level stamp is a roll-up. On a folio whose capabilities carry their own credentials it
    // goes red as soon as ANY one of them fails, and treating that as a folio-wide failure paused every
    // other setting — an unconfigured FTP feed would lock the real-time settings beside it. Blocking is
    // therefore reserved for the credentials this panel's own block owns: the shared set. A capability's
    // failure is reported on that capability, where it can actually be fixed.
    const folioCheckFailed = folio?.lastValidationSuccess === false;
    // The mirror state: the credentials are proven working, so they are the one thing nobody should be
    // touching — the Common Settings block collapses into an accordion and the day-to-day settings take
    // the space. Never-validated folios keep the flat layout: they are still being set up.
    const folioCheckPassed = folio?.lastValidationSuccess === true;
    // Whether this folio keeps credentials at the folio level at all. A distributor whose catalog feed and
    // API are separate accounts declares none here and carries them on each capability instead, so a
    // "credentials verified" badge on this block would vouch for credentials the block does not contain.
    // The folio-level stamp it comes from is then about the capabilities, not about anything shown here.
    const hasSharedCredentials =
        (folio?.sourceExtensionFolio?.sharedFields?.length ?? 0) > 0;

    /**
     * The capability that owns the import schedule, when exactly one does. The schedule is a single
     * folio-level cron shared by every scheduled capability, so it can only be shown as belonging to one
     * of them when there IS only one — otherwise it stays in the folio's own settings, labelled as
     * covering all of them.
     */
    const scheduledCapabilities = Object.values(
        folio?.sourceExtensionFolio?.subExtensions ?? {})
        .filter(sub => sub.extensionType === 'import');
    const scheduleOwner = scheduledCapabilities.length === 1 ? scheduledCapabilities[0].identifier : undefined;
    /** Named for the subtext when several share it, so it is clear what changing it moves. */
    const scheduledCapabilityNames = scheduledCapabilities.map(sub => sub.name ?? sub.identifier);

    /** Capabilities whose own last check failed, by display name. */
    const failedCapabilities = Object.entries(folio?.subExtensionValidations ?? {})
        .filter(([, stamp]) => stamp?.success === false)
        .map(([identifier]) =>
            folio?.sourceExtensionFolio?.subExtensions?.[identifier]?.name ?? identifier);

    // Only a failing SHARED credential set stops the folio: those are the credentials every capability
    // rides on. Where they live per capability, one failure is that capability's problem.
    const credentialsFailing = folioCheckFailed && hasSharedCredentials;
    // The accordion exists to fold away settings nobody should be touching. That is true of proven
    // credentials, and equally true of a block that holds no credentials at all — a folio whose
    // capabilities carry their own leaves only the occasional folio settings here, so it collapses too.
    // The flat layout is reserved for shared credentials still being set up.
    const credentialsHealthy = hasSharedCredentials ? folioCheckPassed : true;
    // Candidate (edited, unsaved) credentials that failed their automatic check. A local state, never the
    // stored stamp: the stored credentials are untouched and still answer for themselves, but the panel
    // proactively blocks the other settings until the values on screen pass or are abandoned.
    const [candidateFailing, setCandidateFailing] = useState<boolean>(false);
    const [candidateChecking, setCandidateChecking] = useState<boolean>(false);
    const [commonSettingsExpanded, setCommonSettingsExpanded] = useState<boolean>(false);
    const [extensionsExpanded, setExtensionsExpanded] = useState<boolean>(false);
    const [crmSyncExpanded, setCrmSyncExpanded] = useState<boolean>(false);
    const [purchaseHistoryExpanded, setPurchaseHistoryExpanded] = useState<boolean>(false);
    const [subExtensionSettingsExpanded, setSubExtensionSettingsExpanded] = useState<{[id: string]: boolean}>({});
    /**
     * Open the first capability that is still missing required settings, once per folio.
     *
     * Seeded once and never re-applied: re-expanding a box the operator has just collapsed would be the
     * panel arguing with them, and the badge on the header still says what is outstanding either way.
     */
    const seededExpansionFor = useRef<string | undefined>(undefined);
    /** Which capability the panel opened by itself, so only that one starts collapsed to its band. */
    const [autoExpanded, setAutoExpanded] = useState<string | undefined>(undefined);
    const [pendingCredentialEdits, setPendingCredentialEdits] = useState<{ [key: string]: string } | undefined>(undefined);

    // The panel is reused rather than remounted when it is pointed at another folio, so anything held
    // outside the query cache — unsaved credential edits, a validation outcome, which capability was
    // opened — would otherwise carry over from the folio before it and be read as belonging to this one.
    // Not the pivot: that is deep-linkable and resetting it here would fight the ?panelTab= it was opened
    // with. Keyed on the requested id, not the loaded one, so the clearing happens as the switch is made
    // rather than when the new folio lands.
    const requestedFolioId = props.installedExtension?.source ?? props.extensionId;
    const shownFolioId = useRef<string | undefined>(requestedFolioId);
    useEffect(() => {
        if (shownFolioId.current === requestedFolioId) return;
        shownFolioId.current = requestedFolioId;
        setValidationResponse(undefined);
        setPendingCredentialEdits(undefined);
        setCandidateFailing(false);
        setCandidateChecking(false);
        setEnabledSubExtensions(undefined);
        setSubExtensionData(undefined);
        setSubExtensionLabelMap(undefined);
        setSubExtensionDescriptions(undefined);
        setShowMoreErrorInfo(false);
        setShowCategoryMappings(false);
        setMappingSubExtension(undefined);
        setExternalProductSegments([]);
        setSubExtensionSettingsExpanded({});
        setAutoExpanded(undefined);
        seededExpansionFor.current = undefined;
    }, [requestedFolioId]);

    useEffect(() => {
        return () => {
            setSelectedPivot('edit');
            setValidationResponse(undefined);
            setPendingCredentialEdits(undefined);
            setCandidateFailing(false);
            setCandidateChecking(false);
            setEnabledSubExtensions(undefined);
            setSubExtensionData(undefined);
            setSubExtensionLabelMap(undefined);
            setSubExtensionDescriptions(undefined);
            setShowMoreErrorInfo(false);
            setShowCategoryMappings(false);
            setMappingSubExtension(undefined);
            setExternalProductSegments([]);
        }
    }, []);
    
    const close = () => {
        props.onClose();
    }
    useEffect(() => {
        // Segment/type lookups are live credentialed calls to the extension; while the credentials are
        // known bad they can only fail, so they wait for a passing validation (this effect re-runs then).
        if(mappingSubExtension && !credentialsFailing){
            const extensionId = `folio_${props.installedExtension?.source ?? props.extensionId}_${mappingSubExtension.identifier}`;
            mappingFetcher({extensionId: extensionId, field: "externalProductSegments"}).then((response) => {
                if('data' in response && response.data) {
                    const segments = (response.data?.options ?? []).map((data) => {
                        return {
                            segmentName: data.display,
                            id: data.value
                        }
                    })
                    
                    setExternalProductSegments(segments);
                }
            });
            mappingFetcher({extensionId: extensionId, field: "externalProductTypes"}).then((response) => {
                if('data' in response && response.data) {
                    const segments = (response.data?.options ?? []).map((data) => {
                        return {
                            typeName: data.display,
                            id: data.value
                        }
                    })

                    setExternalProductTypes(segments);
                }
            });
        }
    }, [mappingSubExtension, credentialsFailing]);

    useEffect(() => {
        if(folio?.sourceExtensionFolio?.subExtensions) {
            setEnabledSubExtensions(folio?.enabledSubExtensions ?? []);
            const subs = folio?.sourceExtensionFolio?.subExtensions;
            setSubExtensionData(subs ?? []);
            const map: {[key: string]: string} = {}
            const descriptions: {[key: string]: string} = {}
            setShowCategoryMappings(false)

            forEach(subs, (subExtension, key) => {
                if(subExtension.extensionType === 'categoryMapping'){
                    setMappingSubExtension(subExtension);
                    setShowCategoryMappings(true)
                }
                map[subExtension.name] = key;
                if (subExtension.description) descriptions[subExtension.identifier] = subExtension.description;
            });
            setSubExtensionLabelMap(map)
            setSubExtensionDescriptions(descriptions)
        }
    }, [folio?.sourceExtensionFolio?.subExtensions]);
    const [folioUpdater] = usePutExtensionFolioMutation()

    useEffect(() => {
        setSyncAllCompanies(folio?.syncAllCompanies ?? false);
        setSyncAllContacts(folio?.syncAllContacts ?? false);
        setSyncAllLocations(folio?.syncAllLocations ?? false);
        setPurchaseHistoryMonthsBack(folio?.purchaseHistoryMonthsBack ?? DEFAULT_PURCHASE_HISTORY_MONTHS);
        setGlobalStorefrontMinimumPrice(folio?.globalStorefrontMinimumPrice ?? false);
    }, [folio?.syncAllCompanies, folio?.syncAllContacts, folio?.syncAllLocations,
        folio?.purchaseHistoryMonthsBack, folio?.globalStorefrontMinimumPrice]);

    const updateExtensionFolio = (subExtensions: string[]) => {
        if (credentialsFailing) return;
        const existingFolio = cloneDeep(folio)
        if(existingFolio?.sourceExtensionFolio?.id) {
            existingFolio.enabledSubExtensions = subExtensions;
            folioUpdater({extensionId: existingFolio.sourceExtensionFolio.id, request: existingFolio})
        }
    }

    /** Persist a patch to the installed folio (used by the CRM-sync toggles). */
    const saveFolioPatch = (patch: Partial<IInstalledExtensionFolio>) => {
        if (credentialsFailing) return;
        const existingFolio = cloneDeep(folio)
        if(existingFolio?.sourceExtensionFolio?.id) {
            Object.assign(existingFolio, patch);
            folioUpdater({extensionId: existingFolio.sourceExtensionFolio.id, request: existingFolio})
        }
    }

    // The Contact Import sub-extension being enabled gates the CRM-sync settings.
    const contactImportEnabled = !!enabledSubExtensions?.some(id => subExtensionData?.[id]?.extensionType === 'contactImport');
    // Likewise, the look-back window only means anything when something is pulling historical records.
    const purchaseHistoryEnabled = !!enabledSubExtensions?.some(id => subExtensionData?.[id]?.extensionType === 'purchaseHistory');

    // An enabled sub-extension advertising GlobalStorefrontMinimumPrice gates the global-minimum-price option.
    const globalMinimumPriceEnabled = !!enabledSubExtensions?.some(id => subExtensionData?.[id]?.globalStorefrontMinimumPrice === true);

    const [runSync, runSyncState] = useRunCrmFolioSyncMutation();
    const [runFolioImport, runFolioImportState] = useRunImportExtensionsForFolioMutation();
    const importAll = async () => {
        const folioSourceId = folio?.sourceExtensionFolio?.id;
        const folioName = folio?.name ?? 'CRM';
        if (!folioSourceId) return;
        const toastId = toast.loading(`Importing from ${folioName}…`);
        try {
            const result = await runSync(folioSourceId).unwrap();
            const counts = `${result.companies} companies, ${result.contacts} contacts, ${result.locations} locations`;
            if (!result.success && (result.errors?.length ?? 0) > 0) {
                toast.update(toastId, {type: 'warning', render: `${folioName}: imported ${counts} with ${result.errors!.length} error(s)`, isLoading: false, autoClose: 6000});
            } else {
                toast.update(toastId, {type: 'success', render: `${folioName}: ${counts} imported`, isLoading: false, autoClose: 5000});
            }
        } catch {
            toast.update(toastId, {type: 'error', render: `Failed to import from ${folioName}`, isLoading: false, autoClose: 5000});
        }
    };

    const folioContext = props.installedExtension?.source ?? props.extensionId;

    /**
     * Starts one or more of the folio's runnable sub-extensions. These kick off an out-of-process pull, so the
     * toast confirms the request was accepted rather than that the data has landed, progress shows up in the
     * activity list below.
     */
    const startRun = async (subExtensions: string[] | undefined, label: string) => {
        if (!folioContext) return;
        const toastId = toast.loading(`Starting ${label}…`);
        try {
            const result = await runFolioImport({folioId: folioContext, subExtensions}).unwrap();
            if (result.success) {
                toast.update(toastId, {type: 'success', render: `${label} started`, isLoading: false, autoClose: 4000});
            } else {
                toast.update(toastId, {type: 'error', render: result.message || `Failed to start ${label}`, isLoading: false, autoClose: 5000});
            }
        } catch {
            toast.update(toastId, {type: 'error', render: `Failed to start ${label}`, isLoading: false, autoClose: 5000});
        }
    };

    // Sub-extension types the folio run endpoint knows how to start. CRM sync is folio-level rather than a
    // sub-extension run, so it is appended separately below.
    const RUNNABLE_TYPES = ['import', 'purchaseHistory'];
    const runnableSubExtensions = (enabledSubExtensions ?? [])
        .map(id => subExtensionData?.[id])
        .filter((s): s is ISubExtension => !!s && RUNNABLE_TYPES.includes(s.extensionType ?? ''));

    const runTargets: IExtensionRunTarget[] = [
        ...runnableSubExtensions.map(s => ({
            key: s.identifier,
            text: s.name,
            run: () => void startRun([s.identifier], s.name)
        })),
        ...(contactImportEnabled ? [{
            key: '__crmSync',
            text: 'CRM Sync (companies, contacts, locations)',
            run: () => void importAll()
        }] : [])
    ];

    // The identifiers are always sent explicitly: an empty list means "the plain imports only" to the API, which
    // would quietly skip purchase history.
    const runAll = () => {
        if (runnableSubExtensions.length > 0)
            void startRun(runnableSubExtensions.map(s => s.identifier), 'all enabled imports');
        if (contactImportEnabled) void importAll();
    };
    /**
     * How many of a sub-extension's required, actually-rendered settings have no value yet. Uses the
     * shared renderability rule so a run-time field (search etc.) never counts as an unanswered setting
     * under a heading with nothing beneath it, and skips anything a fieldFilter is currently hiding —
     * nobody can fill in what they cannot see, and it becomes required only once the setting that
     * reveals it is turned on.
     *
     * Mirrors MissingSettings on the API side, which is what the management grid's status column
     * reports. The two describe the same form and have to arrive at the same number.
     */
    const missingRequiredSettings = (subExtensionId: string): number => {
        const fields = folio?.sourceExtensionFolio?.subExtensions?.[subExtensionId]?.additionalData ?? [];
        // Its own values over the folio's shared ones, the precedence the folio itself runs on. A
        // capability's filter can point at a shared setting, so the bag has to hold both.
        const values = {
            ...(folio?.commonInstalledFields ?? {}),
            ...(folio?.subExtensionInstalledFields?.[subExtensionId] ?? {})
        };
        const hidden = (field: IAdditionalData) =>
            field.fieldFilter?.filterType === 'hideIfNot' &&
            values[field.fieldFilter.target] !== field.fieldFilter.value;
        return fields.filter(f => isRenderableStaticField(f) && !f.optional && !hidden(f) &&
            (values[f.field] == undefined || values[f.field] === '')).length;
    };

    /**
     * Open the first capability that is still missing required settings, once per folio. Seeded by the
     * effect further down, and declared up here with the rest of the state so switching folios can clear
     * it. See {@link seededExpansionFor}'s effect for why it is seeded only once.
     */
    useEffect(() => {
        const folioId = folio?.id;
        if (!folioId || seededExpansionFor.current === folioId) return;
        if (!enabledSubExtensions) return;
        seededExpansionFor.current = folioId;
        const outstanding = enabledSubExtensions.find(id => missingRequiredSettings(id) > 0);
        if (outstanding) {
            setSubExtensionSettingsExpanded(prev => ({...prev, [outstanding]: true}));
            setAutoExpanded(outstanding);
        }
        // eslint-disable-next-line
    }, [folio?.id, enabledSubExtensions]);

    const renderEnabledSubExtensions = (subExtensionData: { [key: string]: ISubExtension } , enabledSubExtensions: string[]) => {
        const subExtensions : ISubExtension[] = [];
        enabledSubExtensions?.forEach((key) => {
            const subExtension = subExtensionData[key];
            if(subExtension) {
                subExtensions.push(subExtension);

                return subExtension;
            }
        })
        if(!subExtensions || subExtensions.length === 0) return <></>;
        return subExtensions
            // Kept in the folio's own order. Sorting unfinished capabilities to the top was tried and is
            // worse: the boxes move under people who have learned where they are, and filling in the last
            // field makes a capability jump away from where they were just working. Reaching what is
            // unfinished is handled without moving anything — the box is opened for them, and inside it
            // the settings needing input are lifted into a band above the labelled sections.
            .filter(subExtension => enabledSubExtensions.includes(subExtension.identifier))
            .map((subExtension, index) => {
                if(subExtension.extensionType === 'categoryMapping'){
                    return<></>
                }
                // A sub-extension with no renderable settings gets no box: there is nothing to configure,
                // and an empty box under a heading is exactly the noise the renderability rule exists to avoid.
                const subFields = folio?.sourceExtensionFolio?.subExtensions?.[subExtension.identifier]?.additionalData ?? [];
                if (!subFields.some(isRenderableStaticField)) return <React.Fragment key={`subextension-install-${subExtension.identifier}`} />;
                const missing = missingRequiredSettings(subExtension.identifier);
                // This capability's OWN check, not the folio's. Where capabilities carry their own
                // credentials the folio-level stamp is a roll-up, and showing it here would vouch for
                // one capability using another's result. Missing settings still take precedence: an
                // unfinished form is the more immediate thing to say.
                const stamp = folio?.subExtensionValidations?.[subExtension.identifier];
                const verified = stamp?.success === true;
                const capabilityFailed = stamp?.success === false;
                return(
                <AccordionBox
                    key={`subextension-install-${subExtension.identifier}`}
                    title={subExtension.name} boldTitle
                    secondaryTitle={missing > 0
                        ? <MissingRequiredSettingsBadge count={missing} />
                        : verified ? <CredentialsVerifiedBadge />
                        : capabilityFailed ? <CredentialsFailedBadge /> : undefined}
                    isExpanded={!!subExtensionSettingsExpanded[subExtension.identifier]}
                    onToggle={(expanded) => setSubExtensionSettingsExpanded(prev =>
                        ({...prev, [subExtension.identifier]: expanded}))}>
                    <SubExtensionInstallForm
                        folio={folio}
                        subExtension={subExtension.identifier}
                        // Only for the box this panel opened on the operator's behalf. A capability they
                        // expanded themselves is one they came to read, so it shows everything.
                        startOutstandingOnly={autoExpanded === subExtension.identifier}
                        // Nothing left outstanding in a box that was only opened to show what was: close
                        // it. Letting it fall back to the full form would hand over every other setting
                        // as a reward for finishing the two that were missing. Clearing autoExpanded
                        // means re-opening it shows the whole form, which by then is the right thing.
                        onOutstandingCleared={() => {
                            setSubExtensionSettingsExpanded(prev =>
                                ({...prev, [subExtension.identifier]: false}));
                            setAutoExpanded(undefined);
                        }}>
                        {/* The schedule this capability actually runs on, shown with it rather than in the
                            folio's settings — it is the only thing scheduled here. Passed as a child so it
                            hides with the rest of the settings while the box is collapsed to what needs
                            attention; that state lives in the form, which is the only thing that knows it. */}
                        {scheduleOwner === subExtension.identifier && !credentialsFailing &&
                            <FolioScheduleSetting
                                folio={folio}
                                heading='Import Schedule'
                                disabled={credentialsFailing}
                            />}
                    </SubExtensionInstallForm>
                </AccordionBox>
            )});
    };
    
    /**
     * Every validation outcome lands here. The API stamped the outcome on the installed record during
     * the call, so the cache is patched to match — that is also what enters and leaves validate-first
     * mode without a refetch. A pass while blocked is the moment the unsaved credential edits persist:
     * they have just been proven against the connector.
     */
    const handleValidationOutcome = (response: IValidateExtensionResponse) => {
        setValidationResponse(response);
        const sourceId = folio?.sourceExtensionFolio?.id;
        if (!sourceId) return;
        // Mirror of the API's stamping rule: a failed check of candidate (unsaved) values does not stamp
        // the record — the stored credentials' last real answer stands — so the cache is not patched to
        // failing either. The panel blocks locally instead, and the pending edits stay pending; nothing
        // about the stored state changed.
        if (pendingCredentialEdits && !response.success) {
            setCandidateFailing(true);
            return;
        }
        setCandidateFailing(false);
        dispatch(api.util.updateQueryData('getExtensionFolio', sourceId, (draft) => {
            draft.lastValidationSuccess = response.success;
            draft.lastValidationMessage = response.message ?? response.error;
        }));
        // Pending credential edits exist in both validate-first flows — fixing bad credentials and
        // changing good ones — and in both, the pass is the moment the proven values persist.
        if (response.success && pendingCredentialEdits && folio) {
            folioUpdater({
                extensionId: sourceId,
                request: {
                    ...folio,
                    commonInstalledFields: pendingCredentialEdits,
                    lastValidationSuccess: true,
                    lastValidationMessage: response.message ?? undefined,
                }
            });
            setPendingCredentialEdits(undefined);
        }
    };

    // Edited credential values are checked automatically, a couple of seconds after the last edit, so
    // nobody has to know to press Validate: a pass applies them (handleValidationOutcome saves proven
    // values), a fail blocks proactively. Latest-callback ref because the debounced wrapper is created
    // once and must not capture stale state.
    const [validateFolioCandidate] = useValidateExtensionFolioMutation();
    const runCandidateValidationRef = useRef<(fields: { [key: string]: string }) => void>();
    runCandidateValidationRef.current = async (fields) => {
        const data = folio;
        const sourceId = data?.sourceExtensionFolio?.id;
        if (!sourceId || !data?.id) return;
        setCandidateChecking(true);
        const result = await validateFolioCandidate({
            id: sourceId,
            request: {
                additionalData: fields,
                folioId: data.id,
                validationActions: data.enabledSubExtensions,
            }
        });
        setCandidateChecking(false);
        if (IsMutationSuccess<IValidateExtensionResponse>(result)) handleValidationOutcome(result.data);
        else handleValidationOutcome({success: false, message: 'Failed to communicate with API'});
    };
    const debouncedCandidateValidation = useMemo(
        () => debounce((fields: { [key: string]: string }) => runCandidateValidationRef.current?.(fields), 2000),
        []);
    useEffect(() => () => debouncedCandidateValidation.cancel(), [debouncedCandidateValidation]);

    const handleCredentialEdit = (fields: { [key: string]: string }) => {
        setPendingCredentialEdits(fields);
        debouncedCandidateValidation(fields);
    };

    const getMessagebar = () => {
        return(<div>
        {validationResponse && !validationResponse.success &&
        <MessageBar messageBarType={MessageBarType.error}>
            {validationResponse.error}
            {validationResponse.validationMessages && validationResponse.validationMessages.length > 0 && <Link onClick={() => {
                setShowMoreErrorInfo(true);
            }}>&nbsp;More Info</Link>}
        </MessageBar>
        }
        {validationResponse?.success &&
            <div>
                {validationResponse.validationMessages && validationResponse.validationMessages.length > 0 ?
                    <MessageBar messageBarType={MessageBarType.warning} style={{paddingBottom: "10px"}}>
                        {validationResponse.message ?? 'Extension settings validated with warnings'}
                        {validationResponse.validationMessages && validationResponse.validationMessages.length > 0 && <Link onClick={() => {
                            setShowMoreErrorInfo(true);
                        }}>&nbsp;More Info</Link>}
                    </MessageBar>:
                    <MessageBar messageBarType={MessageBarType.success} style={{paddingBottom: "10px"}}>
                        {validationResponse.message ?? 'Extension settings validated'}
                    </MessageBar>
                }

            </div>}
        </div>)
    }
    
    const categoryFetcher = async (segmentId: string, subExtension: string) => {
        const categories : {categoryName: string, id: string}[] = []
        const extensionId = `folio_${props.installedExtension?.source ?? props.extensionId}_${subExtension}`;
        const additionalData = {["category"]: segmentId }
        const result = await mappingFetcher({extensionId: extensionId, field: "externalProductCategories", additionalData: additionalData}).then()
        // `options` is absent when the metadata call fails or the extension has no categories to
        // offer; without this the whole page went down with an uncaught TypeError.
        if(result.data?.options){
            result.data.options.forEach((data) => {
                categories.push({
                    categoryName: data.display,
                    id: data.value
                })
            })
        }
        return categories;
    }
    
    return (
        <React.Fragment>
            <ExtensionValidationErrorModal errorDetails={validationResponse} displayModal={showMoreErrorInfo} closer={() => setShowMoreErrorInfo(false)} />
            {(props.installedExtension?.source && mappingSubExtension) &&
            <ExtensionCategoryMappingModal 
                context={props.installedExtension?.source}
                subExtension={mappingSubExtension?.identifier}
                externalSegments={externalProductSegments} 
                getExternalCategory={categoryFetcher} 
                externalTypes={externalProductTypes} 
                onClose={() => setShowCategoryMappingsModal(false)} 
                isOpen={showCategoryMappingsModal}
                segmentFilter={mappingSegmentFilter}
            />
            }
            <Panel
                onOuterClick={() => {
                    console.log("swallowing modal close event")
                }}
                isOpen={props.isOpen}
                headerText={props.isNew ? 'Install Extension' : 'Extension Folio Details'}
                isFooterAtBottom={true}
                type={PanelType.medium}
                onDismiss={close}
                onRenderHeader={() => (<div style={{flexGrow: 1, paddingLeft: '25px', background: "white"}}>

                    <Stack horizontal disableShrink>
                        <Stack.Item align='auto'>
                            <Image imageFit={ImageFit.contain}
                                   src={folio?.sourceExtensionFolio?.image ?? PLACEHOLDER_IMAGE}
                                   height={150}
                                   width={150}
                                   className={'product-image'}
                                   title={"Extension Image"}/>
                        </Stack.Item>
                        <Stack.Item align='center' style={{paddingLeft: '1em'}}>
                            <Text variant={'xLarge'} block>
                                {folio?.sourceExtensionFolio?.name}
                            </Text>

                        </Stack.Item>
                    </Stack>
                </div>)}
                onRenderFooter={() => (
                    <div style={{ position: 'sticky', bottom: 0, background: 'white', zIndex: 1, padding: '1em', boxShadow: '0 -2px 5px rgba(0,0,0,0.1)' }}>
                        {getMessagebar()}
                        {selectedPivot === 'edit' &&
                            <ExtensionFolioInstallButtons
                                extensionFolio={folio}
                                overrideCommonFields={pendingCredentialEdits}
                                onValidate={handleValidationOutcome}
                                onClose={close}
                            />}
                    </div>
                )}
            >
                {folio === undefined ? extensionFolio.isFetching ? <>Loading</> :
                    <p>An error occurred loading the extension folio. Please try again.</p>
                    : (
                        <Pivot
                            selectedKey={selectedPivot}
                            onLinkClick={(item) => {
                                if (item?.props?.itemKey) setSelectedPivot(item.props.itemKey)
                            }}
                            overflowBehavior='menu'
                            styles={{itemContainer: '1em'}}
                        >
    
                            <PivotItem headerText='Extension Folio Settings' itemKey={'edit'}>
                                {!hasSharedCredentials && failedCapabilities.length > 0 &&
                                    <MessageBar messageBarType={MessageBarType.severeWarning} styles={{root: {marginTop: '1em'}}}>
                                        {failedCapabilities.length === 1
                                            ? `${failedCapabilities[0]} could not be verified.`
                                            : `${failedCapabilities.join(', ')} could not be verified.`}
                                        {' '}Open it under Extensions below and fix its credentials. The rest of
                                        this folio is unaffected — each of these connects with its own account.
                                    </MessageBar>}
                                {credentialsFailing &&
                                    <MessageBar messageBarType={MessageBarType.severeWarning} styles={{root: {marginTop: '1em'}}}>
                                        The last credential check for this folio failed
                                        {folio?.lastValidationMessage ? `: ${folio.lastValidationMessage}` : '.'}
                                        {' '}Fix the credentials below and press Validate — other settings and
                                        option lookups are paused until a check passes.
                                    </MessageBar>}
                                {/* A folio-level failure that neither of the bars above accounts for: no
                                    shared credentials to blame it on, and no capability carrying a stamp of
                                    its own — which is what a check run before per-capability stamping, or
                                    one that failed before it reached a capability, leaves behind.

                                    Shown rather than swallowed. Without this the management grid said the
                                    connection needed attention while the panel it sends you to showed
                                    nothing wrong at all, and the only way to see the connector's reason was
                                    to hover a tooltip in a grid you had already left. */}
                                {folioCheckFailed && !hasSharedCredentials && failedCapabilities.length === 0 &&
                                    <MessageBar messageBarType={MessageBarType.severeWarning} styles={{root: {marginTop: '1em'}}}>
                                        The last check of this connection failed
                                        {folio?.lastValidationMessage ? `: ${folio.lastValidationMessage}` : '.'}
                                        {' '}This folio keeps its credentials on each capability, so the
                                        problem is with one of those below — open it and press Validate to
                                        find out which. A field that is filled in can still be wrong.
                                    </MessageBar>}
                                {credentialsHealthy ?
                                    <div style={{marginTop: '1em'}}>
                                    <AccordionBox title='Common Settings' boldTitle
                                        secondaryTitle={hasSharedCredentials ? <CredentialsVerifiedBadge /> : undefined}
                                        isExpanded={commonSettingsExpanded}
                                        onToggle={(expanded) => setCommonSettingsExpanded(expanded)}>
                                        <ExtensionFolioInstallForm
                                            folio={folio}
                                            validateFirstCredentials
                                            hideSchedule={!!scheduleOwner}
                                            scheduleAppliesTo={scheduledCapabilityNames}
                                            onBlockedFieldEdit={handleCredentialEdit}
                                        />
                                    </AccordionBox>
                                    {/* Outside the box so collapsing it cannot hide that edits are in limbo. */}
                                    {pendingCredentialEdits && !candidateFailing &&
                                        <MessageBar messageBarType={MessageBarType.info}>
                                            {candidateChecking
                                                ? 'Checking the new credentials…'
                                                : 'Credential changes are checked automatically and saved once the check passes.'}
                                        </MessageBar>}
                                    {candidateFailing &&
                                        <MessageBar messageBarType={MessageBarType.severeWarning}>
                                            The new credentials failed their check and were not saved — the
                                            existing working credentials are still in place. Fix the values
                                            (they re-check automatically), or close the panel to keep the
                                            current credentials. Other settings are paused meanwhile.
                                        </MessageBar>}
                                    </div>
                                    : <>
                                        <h3 style={{marginBottom: 0, marginTop: '1em'}}>Common Settings</h3>

                                        <ExtensionFolioInstallForm
                                            folio={folio}
                                            blockSaves={credentialsFailing}
                                            hideSchedule={!!scheduleOwner}
                                            scheduleAppliesTo={scheduledCapabilityNames}
                                            onBlockedFieldEdit={handleCredentialEdit}
                                        />
                                    </>}
                                {!credentialsFailing && !candidateFailing && <>
                                {opportunityImportExtensionId && <>
                                    <h3 style={{marginBottom: 0, marginTop: '1em'}}>PSA Quote Link</h3>
                                    <Text block styles={{root: {marginBottom: '0.5em'}}}>
                                        {folioQuoteLink?.description ??
                                            'Add a quote link to your PSA that opens Adaptive Catalog and imports the opportunity into a prepared cart automatically.'}
                                    </Text>
                                    <DefaultButton
                                        text='Get Quote Link'
                                        iconProps={{iconName: 'Link'}}
                                        onClick={() => setQuoteLinkOpen(true)}
                                    />
                                    <PsaQuoteLinkDialog
                                        isOpen={quoteLinkOpen}
                                        onClose={() => setQuoteLinkOpen(false)}
                                        extensionId={opportunityImportExtensionId}
                                        folioId={props.installedExtension?.source ?? props.extensionId}
                                        parameter={folioQuoteLink?.parameter}
                                        description={folioQuoteLink?.description}
                                        instructions={folioQuoteLink?.instructions}
                                    />
                                </>}
                                <div style={{marginTop: '1em'}}>
                                <AccordionBox title='Extensions' boldTitle
                                    secondaryTitle={<span style={{marginLeft: '0.5em', fontWeight: 400}}>
                                        ({enabledSubExtensions?.length ?? 0} of {Object.keys(subExtensionLabelMap ?? {}).length} enabled)
                                    </span>}
                                    isExpanded={extensionsExpanded}
                                    onToggle={(expanded) => setExtensionsExpanded(expanded)}>
                                    {subExtensionLabelMap && <LabeledCheckboxGroup
                                        options={subExtensionLabelMap}
                                        selected={enabledSubExtensions ?? []}
                                        onChange={(selected) => {
                                            setEnabledSubExtensions(selected);
                                            updateExtensionFolio(selected)
                                        }}
                                        descriptions={subExtensionDescriptions}
                                    />}
                                </AccordionBox>
                                </div>
                                {(subExtensionData && enabledSubExtensions) && renderEnabledSubExtensions(subExtensionData, enabledSubExtensions)}

                                {globalMinimumPriceEnabled &&
                                    <Checkbox
                                        styles={{root: {marginTop: '1em'}}}
                                        label='Import Product Price as Global Storefront Minimum Price'
                                        checked={globalStorefrontMinimumPrice}
                                        onChange={(e, checked) => { setGlobalStorefrontMinimumPrice(!!checked); saveFolioPatch({globalStorefrontMinimumPrice: !!checked}); }}
                                    />}

                                <ConditionalSetting
                                    when={contactImportEnabled}
                                    idPrefix={[
                                        `extension.${props.installedExtension?.source ?? props.extensionId}.syncAllCompanies`,
                                        `extension.${props.installedExtension?.source ?? props.extensionId}.syncAllLocations`,
                                        `extension.${props.installedExtension?.source ?? props.extensionId}.syncAllContacts`,
                                    ]}
                                >
                                    <div style={{marginTop: '1em'}}>
                                    <AccordionBox title='CRM Sync' boldTitle
                                        secondaryTitle={<span style={{marginLeft: '0.5em', fontWeight: 400}}>
                                            ({[syncAllCompanies, syncAllLocations, syncAllContacts].filter(Boolean).length} of 3 sync-all options on)
                                        </span>}
                                        isExpanded={crmSyncExpanded}
                                        onToggle={(expanded) => setCrmSyncExpanded(expanded)}>
                                    <DynamicSetting id={`extension.${props.installedExtension?.source ?? props.extensionId}.syncAllCompanies`}>
                                    <Toggle
                                        label='Sync all companies'
                                        checked={syncAllCompanies}
                                        onText='Every company referencing this CRM is synced (unless individually excluded)'
                                        offText='Only companies individually set to sync are synced'
                                        onChange={(e, checked) => { setSyncAllCompanies(!!checked); saveFolioPatch({syncAllCompanies: !!checked}); }}
                                    />
                                    </DynamicSetting>
                                    <DynamicSetting id={`extension.${props.installedExtension?.source ?? props.extensionId}.syncAllLocations`}>
                                    <Toggle
                                        label='Sync all locations for imported companies'
                                        checked={syncAllLocations}
                                        onChange={(e, checked) => { setSyncAllLocations(!!checked); saveFolioPatch({syncAllLocations: !!checked}); }}
                                    />
                                    </DynamicSetting>
                                    <DynamicSetting id={`extension.${props.installedExtension?.source ?? props.extensionId}.syncAllContacts`}>
                                    <Toggle
                                        label='Sync all contacts for imported companies'
                                        checked={syncAllContacts}
                                        onChange={(e, checked) => { setSyncAllContacts(!!checked); saveFolioPatch({syncAllContacts: !!checked}); }}
                                    />
                                    </DynamicSetting>
                                    <DefaultButton
                                        text='Import All'
                                        iconProps={{iconName: 'CloudImportExport'}}
                                        disabled={runSyncState.isLoading}
                                        onClick={() => void importAll()}
                                        styles={{root: {marginTop: '1em'}}}
                                    />
                                    </AccordionBox>
                                    </div>
                                </ConditionalSetting>

                                <ConditionalSetting
                                    when={purchaseHistoryEnabled}
                                    idPrefix={`extension.${props.installedExtension?.source ?? props.extensionId}.purchaseHistoryMonthsBack`}
                                >
                                    <div style={{marginTop: '1em'}}>
                                    <AccordionBox title='Purchase History' boldTitle
                                        secondaryTitle={<span style={{marginLeft: '0.5em', fontWeight: 400}}>
                                            ({purchaseHistoryMonthsBack} months of history)
                                        </span>}
                                        isExpanded={purchaseHistoryExpanded}
                                        onToggle={(expanded) => setPurchaseHistoryExpanded(expanded)}>
                                    <DynamicSetting id={`extension.${props.installedExtension?.source ?? props.extensionId}.purchaseHistoryMonthsBack`}>
                                    <SpinButton
                                        label='Months of history to import'
                                        min={1}
                                        max={120}
                                        step={1}
                                        value={String(purchaseHistoryMonthsBack)}
                                        onChange={(_e, value) => {
                                            const months = Number(value);
                                            if (!Number.isFinite(months) || months < 1) return;
                                            setPurchaseHistoryMonthsBack(months);
                                            saveFolioPatch({purchaseHistoryMonthsBack: months});
                                        }}
                                        styles={{root: {maxWidth: 320}}}
                                    />
                                    <Text variant='small' styles={{root: {color: '#605e5c'}}}>
                                        Only used the first time this runs. After that, each run imports the
                                        records that changed since the previous one.
                                    </Text>
                                    </DynamicSetting>
                                    </AccordionBox>
                                    </div>
                                </ConditionalSetting>
                                </>}

                            </PivotItem>

                            {showCategoryMappings && !credentialsFailing && !candidateFailing && props.installedExtension?.source &&

                                <PivotItem headerText={'Product Mappings'} itemKey={'segmentMappings'}>
                                    <ExtensionSegmentMappingTable 
                                        context={props.installedExtension.source} 
                                        externalSegments={externalProductSegments} 
                                        externalTypes={externalProductTypes} 
                                        onSelectSegment={(segment) => {
                                            setMappingSegmentFilter(segment.id);
                                            setShowCategoryMappingsModal(true);
                                        }}
                                    />
                                </PivotItem>
                            }
                            {(props.installedExtension?.source ?? props.extensionId) && <PivotItem headerText='Activity' itemKey={'logs'}>
                                <ExtensionActivities
                                    context={props.installedExtension?.source ?? props.extensionId}
                                    subExtensions={subExtensionData
                                        ? Object.values(subExtensionData).map(s => ({ identifier: s.identifier, name: s.name }))
                                        : undefined}
                                    runTargets={runTargets}
                                    onRunAll={runAll}
                                    runDisabled={runFolioImportState.isLoading || runSyncState.isLoading}
                                />
                            </PivotItem>}
                        </Pivot>
                    )}
            </Panel>
        </React.Fragment>
    );
}

export default ExtensionFolioDetailsPanel;
